import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { formatCurrency, type SupportedCurrency } from "@utils/currencyUtils";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useGetDearProducts } from "@queries/useGetDearProducts";
import { useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import LoadingOverlay from "@components/LoadingOverlay";
import { getAccessoryCategoriesFromProducts } from "@utils/accessoryCategoryUtils";
import type { OrganizationProducts } from "@api/private/getOrganization";

export default function Accessories() {
  const { watch, setValue } = useFormContext();
  const navigate = useNavigate();
  const selectedCurrency = (watch("selectedCurrency") as SupportedCurrency) || "usd";
  const items = watch("items") || [];
  const accessories = watch("accessories") || {};
  const organizationProducts = (watch("organizationProducts") as OrganizationProducts) || {};

  // Query accessory categories based on user's paid subscriptions
  const categories = useMemo(
    () => getAccessoryCategoriesFromProducts(organizationProducts),
    [organizationProducts],
  );
  const { data, isLoading } = useGetDearProducts(categories, selectedCurrency);
  const products = data?.products || [];

  useEffect(() => {
    // Store products in form context so Cart can access them
    setValue("accessoryProducts", products);
  }, [products, setValue]);

  const updateQuantity = (sku: string, delta: number) => {
    const currentQty = accessories[sku] || 0;
    const newQty = Math.max(0, currentQty + delta);

    const newAccessories = { ...accessories };

    if (newQty === 0) {
      delete newAccessories[sku];
    } else {
      newAccessories[sku] = newQty;
    }

    setValue("accessories", newAccessories);

    // Also update items array for checkout
    const existingItems = items || [];
    const filtered = existingItems.filter((item: { sku: string }) => item.sku !== sku);

    if (newQty > 0) {
      filtered.push({ sku, quantity: newQty });
    }

    setValue("items", filtered);
    // CartInfoContext automatically computes optimistic line items from accessories and items
  };

  const getProductQuantity = (sku: string) => {
    return accessories[sku] || 0;
  };

  const handleCheckout = () => {
    // Reset to step 1 and navigate to home
    setValue("currentStep", 1);
    navigate("/");
  };

  const totalAccessoriesInCart = Object.values(accessories as Record<string, number>).reduce(
    (sum: number, qty: number) => sum + qty,
    0,
  );

  if (isLoading) {
    return <LoadingOverlay open={true} />;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          Shop Accessories
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Browse our selection of accessories for SSP, RRP, and ILS. Add items to your cart and
          proceed to checkout.
        </Typography>
      </Box>

      {products.length === 0 ? (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="text.secondary">
            No accessories available at this time.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {products.map((product) => {
            const quantity = getProductQuantity(product.SKU);
            const thumbnail = (product as any).Attachments?.[0];

            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={product.SKU}>
                <Card
                  variant="outlined"
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {thumbnail && (
                    <Box
                      sx={{
                        width: "100%",
                        height: "200px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f5f5f5",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={thumbnail}
                        alt={product.Name}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {product.Name}
                    </Typography>
                    {(product as any).Description && (
                      <Typography color="text.secondary" gutterBottom variant="body2">
                        {(product as any).Description}
                      </Typography>
                    )}
                    <Typography variant="h5" color="primary" gutterBottom>
                      {formatCurrency(product.PriceTier1, selectedCurrency)}
                    </Typography>
                  </CardContent>
                  <Box p={2} pt={0}>
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
                      <IconButton
                        onClick={() => updateQuantity(product.SKU, -1)}
                        disabled={quantity === 0}
                        color="primary"
                        size="small"
                      >
                        <RemoveIcon />
                      </IconButton>
                      <Typography variant="h6" sx={{ minWidth: "40px", textAlign: "center" }}>
                        {quantity}
                      </Typography>
                      <IconButton
                        onClick={() => updateQuantity(product.SKU, 1)}
                        color="primary"
                        size="small"
                      >
                        <AddIcon />
                      </IconButton>
                    </Stack>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {totalAccessoriesInCart > 0 && (
        <Box mt={4}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">
                  {totalAccessoriesInCart} item{totalAccessoriesInCart !== 1 ? "s" : ""} in cart
                </Typography>
                <Button variant="contained" size="large" onClick={handleCheckout}>
                  Proceed to Checkout
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )}
    </Container>
  );
}
